import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  styled,
  alpha,
  useTheme,
  TextField,
  Button,
  Chip,
  InputAdornment,
  Collapse,
  Checkbox,
} from "@mui/material";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import NotificationSnackbar from "./NotificationBar"; // Ensure this path matches your project structure

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&::-webkit-scrollbar": { height: "8px", width: "8px" },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.4) },
  },
}));

function EmployeeBonus() {
  const theme = useTheme();
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";

  const [searchTerm, setSearchTerm] = useState("");
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showData, setShowData] = useState(false);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleSearch = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setShowData(false);
      setBonuses([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res = await fetch(
        `/api/support/employee-rbm-bonus?employeeId=${term}`
      );
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const msg = (data && data.message) || `HTTP ${res.status}`;
        setError(msg);
        setShowData(true);
        setBonuses([]);
        return;
      }

      const list = Array.isArray(data) ? data : [];
      if (list.length > 0) {
        const sortedList = list.sort((a, b) => {
          const yearA = a.fiscalYearId || "";
          const yearB = b.fiscalYearId || "";
          return yearB.localeCompare(yearA);
        });

        setBonuses(sortedList);
        setShowData(true);
        setError(null);
      } else {
        setBonuses([]);
        setShowData(true);
        setError("No bonuses found for this Employee ID.");
      }
    } catch (err) {
      console.error("Search failed:", err);
      setError("Search failed. Please try again.");
      setShowData(true);
      setBonuses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setError(null);
    setShowData(false);
    setBonuses([]);
  };

  const handleToggleIgnoreSixMonths = async (bonusId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const response = await fetch("/api/support/ignore-six-months", {
        method: "PUT", 
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: bonusId,
          ignoreSixMonths: newStatus,
        }),
      });

      if (response.ok) {
        setSnackbarMessage(
          `Ignore Six Months set to ${newStatus ? "True" : "False"} successfully!`
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);

        // Update the local state to reflect the change visually
        setBonuses((prevBonuses) =>
          prevBonuses.map((b) =>
            b.id === bonusId ? { ...b, ignoreSixMonths: newStatus } : b
          )
        );
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSnackbarMessage(
          errorData.message || "Failed to update Ignore Six Months status."
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error updating Ignore Six Months:", error);
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        minHeight: "75vh",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          mb: 4,
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY_COLOR }}>
          Employee Bonus Management
        </Typography>
      </Box>

      {/* SEARCH CARD */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border: `1px solid ${alpha(PRIMARY_COLOR, 0.1)}`,
          bgcolor: alpha(PRIMARY_COLOR, 0.015),
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 1, color: PRIMARY_COLOR, fontWeight: 600 }}
        >
          Search Employee Bonus
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Enter an Employee ID to view all of their historical performance bonuses.
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 3,
          }}
        >
          <TextField
            placeholder="Enter Employee ID"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              const v = e.target.value;
              setSearchTerm(v);
              if (!v.trim()) setShowData(false);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm && (
                    <IconButton
                      aria-label="clear"
                      size="small"
                      onClick={handleClearSearch}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              maxWidth: 500,
              bgcolor: "white",
              borderRadius: 1,
            }}
          />
          <Button
            variant="contained"
            disableElevation
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SearchIcon />
              )
            }
            onClick={handleSearch}
            disabled={loading}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              bgcolor: PRIMARY_COLOR,
              "&:hover": { bgcolor: SECONDARY_COLOR },
            }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </Box>

        <Collapse in={showData} timeout="auto">
          {error ? (
            <Typography
              color="error"
              sx={{ fontSize: "0.95rem", fontWeight: 500 }}
            >
              {error}
            </Typography>
          ) : bonuses && bonuses.length > 0 ? (
            <Box>
              <Typography
                sx={{ fontWeight: 600, color: PRIMARY_COLOR, mb: 1.5 }}
              >
                Found {bonuses.length} Bonus Record
                {bonuses.length > 1 ? "s" : ""}
              </Typography>
              <ResponsiveTableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Employee ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        First Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Last Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Position
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Fiscal Year
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Score
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Rate
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Category
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Ignore 6 Months
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bonuses.map((b) => (
                      <TableRow key={b.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {b.employeeId ?? "-"}
                        </TableCell>
                        <TableCell>{b.firstName || "-"}</TableCell>
                        <TableCell>{b.lastName || "-"}</TableCell>
                        <TableCell>{b.Position || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            label={`FY ${b.fiscalYearId}`}
                            size="small"
                            sx={{
                              bgcolor: alpha(SECONDARY_COLOR, 0.1),
                              color: SECONDARY_COLOR,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color:
                                b.score >= 70
                                  ? "success.main"
                                  : "warning.main",
                            }}
                          >
                            {b.score ?? 0}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {b.rate ?? "-"}
                        </TableCell>
                        <TableCell>
                           <Chip
                              label={b.category || "Unknown"}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 600, color: PRIMARY_COLOR, borderColor: alpha(PRIMARY_COLOR, 0.3) }}
                            />
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={b.ignoreSixMonths}
                            onChange={() => handleToggleIgnoreSixMonths(b.id, b.ignoreSixMonths)}
                            sx={{
                              color: alpha(PRIMARY_COLOR, 0.4),
                              "&.Mui-checked": {
                                color: PRIMARY_COLOR,
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTableContainer>
            </Box>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>
              No results found.
            </Typography>
          )}
        </Collapse>
      </Paper>

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default EmployeeBonus;